<template>
  <div class="bitts-checkbox">
    <checkbox
      v-bind="{ ...accessibleNameAttrs, ...checkedStateAttrs }"
      :class="{
        'bitts-checkbox-with-label': !!label,
      }"
      :disabled="disabled"
      @change="onChange"
    >
      <template v-if="label">
        <span class="bitts-checkbox-label">
          {{ label }}
        </span>
      </template>
      <slot class="bitts-checkbox-label" name="label" />
    </checkbox>
  </div>
</template>

<script setup lang="ts">
import { Checkbox } from 'ant-design-vue';
import { computed, useSlots } from 'vue';

import { useMissingNameWarning } from '../composables/useMissingNameWarning';

interface Props {
  checked?: boolean;
  defaultChecked?: boolean;
  disabled?: boolean;
  indeterminate?: boolean;
  label?: string;
  // Name for a checkbox with no visible label (a table row, a select-all).
  // Prefer ariaLabelledby pointing at visible text over ariaLabel.
  ariaLabel?: string;
  ariaLabelledby?: string;
}

const props = withDefaults(defineProps<Props>(), {
  checked: false,
  defaultChecked: false,
  disabled: false,
  indeterminate: false,
  label: '',
});

const emit = defineEmits<{
  input: [value: boolean];
}>();

const slots = useSlots();

// Ant's Checkbox forwards aria-* to its input. A visible label already names
// the input through Ant's wrapping <label>, so it wins over the aria props.
const accessibleNameAttrs = computed(() =>
  props.label || slots.label
    ? {}
    : {
        'aria-label': props.ariaLabel,
        'aria-labelledby': props.ariaLabelledby,
      },
);

/**
 * One checkbox for both modes: swapping between a controlled and an
 * uncontrolled instance replaced the focused input on every toggle and
 * dropped focus to the page.
 */
const checkedStateAttrs = computed(() =>
  props.checked
    ? { checked: true }
    : {
        defaultChecked: props.defaultChecked,
        indeterminate: props.indeterminate,
      },
);

useMissingNameWarning({
  isNamed: !!(
    props.label ||
    slots.label ||
    props.ariaLabel ||
    props.ariaLabelledby
  ),
  component: 'BittsCheckbox',
  role: 'checkbox',
  hint: 'set label, the #label slot, ariaLabel, or ariaLabelledby',
});

function onChange(e: { target: { checked: boolean } }) {
  emit('input', e.target.checked);
}
</script>

<style lang="pcss">
@reference "../../../../apps/crossbeam-app/src/css/theme.css";
.bitts-checkbox {
  .ant-checkbox {
    @apply top-0;
  }

  .bitts-checkbox-with-label {
    .ant-checkbox {
      @apply leading-none mb-2;
    }
  }
  .bitts-checkbox-label {
    @apply flex items-center;
  }
  .ant-checkbox-wrapper {
    @apply font-normal flex items-center;
    font-family: inherit;
  }

  .ant-checkbox-wrapper:hover {
    @apply text-secondary-background-medium;
  }

  .ant-checkbox-wrapper:not(.ant-checkbox-wrapper-disabled):hover
    .ant-checkbox-checked:not(.ant-checkbox-disabled)
    .ant-checkbox-inner {
    @apply bg-secondary-background-medium;
  }

  .ant-checkbox-checked::after {
    @apply border-none;
  }

  .ant-checkbox-inner {
    @apply border transition-none border-neutral-border-strong rounded-3;
  }

  .ant-checkbox-checked .ant-checkbox-inner {
    @apply bg-secondary-background-medium border-secondary-background-medium border-2;
  }

  .ant-checkbox-checked .ant-checkbox-inner::after {
    @apply w-[4px] h-[9px];
    border-width: 1.8px;
    top: 50%;
    left: 15%;
  }

  .ant-checkbox-wrapper:hover .ant-checkbox-inner,
  .ant-checkbox:hover .ant-checkbox-inner,
  .ant-checkbox-inner:hover {
    @apply border-2 border-secondary-background-medium;
  }

  .ant-checkbox-input:focus + .ant-checkbox-inner {
    @apply border-neutral-border-focus border-2;
  }

  .ant-checkbox-disabled {
    .ant-checkbox-inner {
      @apply cursor-not-allowed;
      @apply bg-neutral-background-disabled;
    }
    &.ant-checkbox-checked {
      .ant-checkbox-inner::after {
        @apply border-neutral-border-focus;
      }
      .ant-checkbox-inner {
        @apply border-2 rounded-3;
        @apply border-neutral-background-disabled!;
      }
    }
    &:not(.ant-checkbox-checked) {
      .ant-checkbox-inner {
        @apply bg-neutral-background-disabled;
        @apply border-neutral-background-disabled!;
      }
    }
  }

  .ant-checkbox-indeterminate {
    .ant-checkbox-inner {
      &:after {
        @apply bg-secondary-background-medium;
      }
      &:hover {
        @apply border-secondary-background-medium;
      }
      &:focus {
        @apply border-neutral-border-focus;
      }
    }
    &.ant-checkbox-disabled {
      @apply cursor-not-allowed bg-neutral-background-disabled;
      .ant-checkbox-inner {
        &:after {
          @apply bg-neutral-accent;
        }
      }
    }
    @apply rounded-4;
  }

  span {
    @apply text-neutral-text-strong;
  }
}
</style>
